
import React, { useState } from 'react';
import { CreatorInfo } from '../CreatorInfo';
import { EditablePriceCell } from '../renderers/KolPriceListCellRenderers';
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { ChevronRight, ChevronDown, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface KolPriceListRowData {
  id: string;
  creatorName: string;
  handler: string | null;
  platform: string | null;
  region: string | null;
  currencyType: string;
  isDuplicate: boolean;
  duplicateCount?: number;
  picIds: string[];
  profileImageUrl?: string;
  kolDbId: string;
  contentTypes: Array<{
    type: string;
    price: number;
  }>;
}

interface KolHierarchicalPriceListRowProps {
  data: KolPriceListRowData;
  onPriceUpdate: (kolDbId: string, contentType: string, newPrice: number) => Promise<void>;
  onCurrencyUpdate: (kolDbId: string, newCurrency: string) => Promise<void>;
  isPending: (id: string, field?: string) => boolean;
  isOptimisticProcessing: boolean;
  showDuplicateInfo?: boolean;
}

export function KolHierarchicalPriceListRow({ 
  data, 
  onPriceUpdate,
  onCurrencyUpdate,
  isPending, 
  isOptimisticProcessing,
  showDuplicateInfo = true
}: KolHierarchicalPriceListRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { hasBuAccess } = useAuth();

  const handlePriceUpdate = async (contentType: string, newPrice: number) => {
    console.log("KolHierarchicalPriceListRow: handlePriceUpdate called", { contentType, newPrice, kolDbId: data.kolDbId });
    await onPriceUpdate(data.kolDbId, contentType, newPrice);
  };

  const handleCurrencyUpdate = async (newCurrency: string) => {
    console.log("KolHierarchicalPriceListRow: handleCurrencyUpdate called", { newCurrency, kolDbId: data.kolDbId });
    await onCurrencyUpdate(data.kolDbId, newCurrency);
  };

  // Currency options
  const currencyOptions = ['MNT', 'USD', 'USDT', 'USDC', 'COOK', 'mETH'];

  // Get the first content type for the main row
  const mainContentType = data.contentTypes[0];
  const subContentTypes = data.contentTypes.slice(1);

  console.log("KolHierarchicalPriceListRow: Rendering with data", { 
    kolDbId: data.kolDbId, 
    creatorName: data.creatorName,
    mainContentType,
    subContentTypes: subContentTypes.length
  });

  return (
    <>
      {/* Main Row */}
      <tr className="border-b hover:bg-muted/50 transition-colors">
        {/* Expand/Collapse Button */}
        <td className="px-4 py-3 w-8">
          {subContentTypes.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              aria-label={isExpanded ? "Collapse content types" : "Expand content types"}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </td>

        {/* Creator Info */}
        <td className="px-4 py-3">
          <div className="flex items-start gap-3">
            <CreatorInfo 
              name={data.creatorName}
              handler={data.handler}
              platform={data.platform}
              region={data.region}
              profileImageUrl={data.profileImageUrl}
            />
            {data.isDuplicate && showDuplicateInfo && (
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {data.duplicateCount || 2}x
                </Badge>
              </div>
            )}
          </div>
        </td>

        {/* Main Content Type */}
        <td className="px-4 py-3">
          {mainContentType ? (
            <Badge variant="secondary" className="text-xs">
              {mainContentType.type}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </td>

        {/* Main Price */}
        <td className="px-4 py-3">
          {mainContentType ? (
            <EditablePriceCell
              row={{
                id: `${data.kolDbId}-${mainContentType.type}`,
                price: mainContentType.price
              }}
              field={mainContentType.type}
              isPending={(id: string) => {
                const pendingState = isPending(data.kolDbId, mainContentType.type);
                console.log("Main EditablePriceCell isPending check", { 
                  id, 
                  kolDbId: data.kolDbId, 
                  contentType: mainContentType.type, 
                  pendingState 
                });
                return pendingState;
              }}
              applyOptimisticUpdate={async (id: string, field: string, value: any) => {
                console.log("Main EditablePriceCell applyOptimisticUpdate called", { 
                  id, 
                  field, 
                  value, 
                  contentType: mainContentType.type 
                });
                return handlePriceUpdate(mainContentType.type, value);
              }}
              isOptimisticProcessing={isOptimisticProcessing}
              currencySymbol={data.currencyType}
            />
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </td>

        {/* Currency Column */}
        <td className="px-4 py-3">
          {hasBuAccess ? (
            <Badge variant="outline" className="text-xs">
              {data.currencyType || 'USD'}
            </Badge>
          ) : (
            <div className="relative">
              {isPending(data.kolDbId, 'currency') && (
                <div className="absolute -top-1 -right-1 z-10">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                </div>
              )}
              <SearchableSelect
                options={currencyOptions}
                value={data.currencyType || 'USD'}
                onChange={handleCurrencyUpdate}
                placeholder="Select currency"
                className="w-full min-w-[100px]"
                badgeVariant="outline"
                isPending={isPending(data.kolDbId, 'currency')}
              />
            </div>
          )}
        </td>

        {/* PICs */}
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {data.picIds.length > 0 ? (
              data.picIds.slice(0, 3).map((pic, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {pic}
                </Badge>
              ))
            ) : (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                No PIC assigned
              </Badge>
            )}
            {data.picIds.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{data.picIds.length - 3}
              </Badge>
            )}
          </div>
        </td>
      </tr>

      {/* Sub Rows for Additional Content Types */}
      {isExpanded && subContentTypes.map((contentType, index) => (
        <tr key={`${data.kolDbId}-sub-${index}`} className="border-b hover:bg-muted/25 transition-colors bg-muted/10">
          {/* Empty expand button column */}
          <td className="px-4 py-3 w-8"></td>

          {/* Indented content indicator */}
          <td className="px-4 py-3">
            <div className="flex items-center gap-2 ml-6">
              <div className="w-2 h-2 bg-muted-foreground/40 rounded-full"></div>
              <span className="text-xs text-muted-foreground">Additional content type</span>
            </div>
          </td>

          {/* Sub Content Type */}
          <td className="px-4 py-3">
            <Badge variant="outline" className="text-xs">
              {contentType.type}
            </Badge>
          </td>

          {/* Sub Price */}
          <td className="px-4 py-3">
            <EditablePriceCell
              row={{
                id: `${data.kolDbId}-${contentType.type}`,
                price: contentType.price
              }}
              field={contentType.type}
              isPending={(id: string) => {
                const pendingState = isPending(data.kolDbId, contentType.type);
                console.log("Sub EditablePriceCell isPending check", { 
                  id, 
                  kolDbId: data.kolDbId, 
                  contentType: contentType.type, 
                  pendingState 
                });
                return pendingState;
              }}
              applyOptimisticUpdate={async (id: string, field: string, value: any) => {
                console.log("Sub EditablePriceCell applyOptimisticUpdate called", { 
                  id, 
                  field, 
                  value, 
                  contentType: contentType.type 
                });
                return handlePriceUpdate(contentType.type, value);
              }}
              isOptimisticProcessing={isOptimisticProcessing}
              currencySymbol={data.currencyType}
            />
          </td>

          {/* Empty Currency column for sub rows */}
          <td className="px-4 py-3"></td>

          {/* Empty PICs column for sub rows */}
          <td className="px-4 py-3"></td>
        </tr>
      ))}
    </>
  );
}
